import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';

const LeadForm = ({ open, onClose, onSubmit, lead = null, loading = false, title = 'Lead Form' }) => {
  const isEdit = Boolean(lead);
  const { states, diseases } = useSelector((state) => state.users);
  
  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      name: lead?.name || '',
      phone: lead?.phone || '',
      email: lead?.email || '',
      state: lead?.state || lead?.state_id || '',
      disease: lead?.disease || lead?.disease_id || '',
      age: lead?.age || '',
      address: lead?.address || '',
      symptoms: lead?.symptoms || '',
      medical_history: lead?.medical_history || '',
      status: lead?.status || 'new',
      priority: lead?.priority || 'medium',
      lead_source: lead?.lead_source || lead?.source || 'manual',
      notes: lead?.notes || ''
    }
  });

  // Reset form when lead prop changes (for edit mode)
  useEffect(() => {
    if (lead) {
      console.log('LeadForm: Lead data received:', lead);
      reset({
        name: lead.name || '',
        phone: lead.phone || '',
        email: lead.email || '',
        state: lead.state || lead.state_id || '',
        disease: lead.disease || lead.disease_id || '',
        age: lead.age || '',
        address: lead.address || '',
        symptoms: lead.symptoms || '',
        medical_history: lead.medical_history || '',
        status: lead.status || 'new',
        priority: lead.priority || 'medium',
        lead_source: lead.lead_source || lead.source || 'manual',
        notes: lead.notes || ''
      });
    }
  }, [lead, reset]);

  const handleFormSubmit = (data) => {
    console.log('LeadForm: Submitting data:', data);
    console.log('LeadForm: Form errors:', errors);

    // Validate that required fields are present
    if (!data.name || !data.phone || !data.state || !data.disease) {
      console.error('LeadForm: Missing required fields:', {
        name: !data.name,
        phone: !data.phone,
        state: !data.state,
        disease: !data.disease
      });
      // Don't submit if required fields are missing
      return;
    }

    // Transform field names to match backend expectations if needed
    const transformedData = {
      ...data,
      state_id: data.state,
      disease_id: data.disease
    };

    console.log('LeadForm: Transformed data being sent to API:', transformedData);

    onSubmit(transformedData);
    if (!isEdit) {
      reset();
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const leadSources = [
    { value: 'manual', label: 'Manual Entry' },
    { value: 'google', label: 'Google Ads' },
    { value: 'meta', label: 'Meta/Facebook' },
    { value: 'website', label: 'Website' },
    { value: 'referral', label: 'Patient Referral' },
    { value: 'doctor_referral', label: 'Doctor Referral' },
    { value: 'phone_inquiry', label: 'Phone Inquiry' },
    { value: 'walk_in', label: 'Walk-in' },
    { value: 'other', label: 'Other' }
  ];

  const leadStatuses = [
    { value: 'new', label: 'New' },
    { value: 'warm', label: 'Warm' },
    { value: 'hot', label: 'Hot' },
    { value: 'followup', label: 'Follow-up' },
    { value: 'ipd_done', label: 'IPD Done' },
    { value: 'opd_done', label: 'OPD Done' },
    { value: 'converted', label: 'Converted' },
    { value: 'dnp', label: 'DNP' },
    { value: 'dead', label: 'Dead' },
    { value: 'junk', label: 'Junk' }
  ];

  const priorities = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' }
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {title || (isEdit ? 'Edit Patient Lead' : 'Create New Patient Lead')}
      </DialogTitle>
      
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          {(!states.length || !diseases.length) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {!states.length && !diseases.length
                ? "States and Diseases data not loaded. Please refresh the page."
                : !states.length
                ? "States data not loaded. Please refresh the page."
                : "Diseases data not loaded. Please refresh the page."
              }
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Patient Name - Required */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Patient name is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Patient Name *"
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    placeholder="Enter patient's full name"
                  />
                )}
              />
            </Grid>
            
            {/* Phone - Required */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="phone"
                control={control}
                rules={{ 
                  required: 'Phone number is required',
                  pattern: {
                    value: /^[0-9]{10}$/,
                    message: 'Please enter a valid 10-digit phone number'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Phone Number *"
                    fullWidth
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                    placeholder="1234567890"
                  />
                )}
              />
            </Grid>
            
            {/* Email */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="email"
                control={control}
                rules={{ 
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    type="email"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    placeholder="patient@example.com"
                  />
                )}
              />
            </Grid>
            
            {/* State - Required */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="state"
                control={control}
                rules={{ required: 'State is required' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.state}>
                    <InputLabel>State *</InputLabel>
                    <Select {...field} label="State *">
                      {states.map((state) => (
                        <MenuItem key={state.id} value={state.id}>
                          {state.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.state && (
                      <Box component="span" sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                        {errors.state.message}
                      </Box>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            
            {/* Disease - Required */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="disease"
                control={control}
                rules={{ required: 'Disease/Condition is required' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.disease}>
                    <InputLabel>Disease/Condition *</InputLabel>
                    <Select {...field} label="Disease/Condition *">
                      {diseases.map((disease) => (
                        <MenuItem key={disease.id} value={disease.id}>
                          {disease.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.disease && (
                      <Box component="span" sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                        {errors.disease.message}
                      </Box>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            
            {/* Age */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="age"
                control={control}
                rules={{ 
                  min: { value: 1, message: 'Age must be at least 1' },
                  max: { value: 120, message: 'Age must be less than 120' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Age"
                    type="number"
                    fullWidth
                    error={!!errors.age}
                    helperText={errors.age?.message}
                    inputProps={{ min: 1, max: 120 }}
                  />
                )}
              />
            </Grid>
            
            {/* Address */}
            <Grid item xs={12}>
              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Address"
                    fullWidth
                    error={!!errors.address}
                    helperText={errors.address?.message}
                    placeholder="Complete address"
                  />
                )}
              />
            </Grid>
            
            {/* Symptoms */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="symptoms"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Symptoms"
                    multiline
                    rows={3}
                    fullWidth
                    error={!!errors.symptoms}
                    helperText={errors.symptoms?.message}
                    placeholder="Describe current symptoms"
                  />
                )}
              />
            </Grid>
            
            {/* Medical History */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="medical_history"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Medical History"
                    multiline
                    rows={3}
                    fullWidth
                    error={!!errors.medical_history}
                    helperText={errors.medical_history?.message}
                    placeholder="Previous medical conditions, surgeries, etc."
                  />
                )}
              />
            </Grid>
            
            {/* Status */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.status}>
                    <InputLabel>Status</InputLabel>
                    <Select {...field} label="Status">
                      {leadStatuses.map((status) => (
                        <MenuItem key={status.value} value={status.value}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            
            {/* Priority */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="priority"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.priority}>
                    <InputLabel>Priority</InputLabel>
                    <Select {...field} label="Priority">
                      {priorities.map((priority) => (
                        <MenuItem key={priority.value} value={priority.value}>
                          {priority.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            
            {/* Lead Source */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="lead_source"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.lead_source}>
                    <InputLabel>Lead Source</InputLabel>
                    <Select {...field} label="Lead Source">
                      {leadSources.map((source) => (
                        <MenuItem key={source.value} value={source.value}>
                          {source.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            
            {/* Notes */}
            <Grid item xs={12}>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Notes"
                    multiline
                    rows={3}
                    fullWidth
                    error={!!errors.notes}
                    helperText={errors.notes?.message}
                    placeholder="Additional notes or comments"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default LeadForm;